import { Component, inject, OnInit, OnDestroy, NgZone } from '@angular/core';
import { TokenService } from './services/token.service';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { AccordionModule } from 'primeng/accordion';
import { CheckboxModule } from 'primeng/checkbox';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ConfigService } from './services/config.service';
import { ListboxModule } from 'primeng/listbox';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { SelectModule } from 'primeng/select';
import { Router, RouterOutlet } from '@angular/router';
import { AuthApi } from './core/services/auth.service';
import { UrlService } from './services/url.service';
import { BuilderStore } from './core/builder/builder.store';
import { GlobalLoadingService } from './core/services/loading.service';
import { BlockUIModule } from 'primeng/blockui';
import { CommonModule } from '@angular/common';
import { PanelModule } from 'primeng/panel';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { HttpClient, HttpContext } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { SKIP_GLOBAL_LOADER } from './core/interceptors/loader.tokens';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    FloatLabelModule,
    InputTextModule,
    ButtonModule,
    AccordionModule,
    CheckboxModule,
    ProgressSpinnerModule,
    ListboxModule,
    PdfViewerModule,
    SelectModule,
    RouterOutlet,
    BlockUIModule,
    ProgressSpinnerModule,
    CommonModule,
    PanelModule,
    ToastModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
    // Version string for deployment verification
    public readonly version = 'wl-consensi-v2026-03-09';
  // --- Services ---
  private authApi = inject(AuthApi);
  private tokens = inject(TokenService);
  private urls = inject(UrlService);
  private config = inject(ConfigService);
  private http = inject(HttpClient);
  private builder = inject(BuilderStore);
  private loader = inject(GlobalLoadingService);
  private router = inject(Router);

  // INJECTED: NgZone is required to ensure events from 'window' trigger UI updates
  private ngZone = inject(NgZone);
  private messageService = inject(MessageService);

  // --- State ---
  loading$ = this.loader.loading$;
  label$ = this.loader.label$;
  templateId = -1;
  title = 'wl-consensi';
  formGroupKeys: string[] = [];
  initialMetadata: any = {};
  sectionToggles: Record<string, boolean> = {};
  isLoading = true;
  optionTextChunks: Record<string, (string | { tag: string })[]> = {};
  pdfModels: any[] = [];
  filteredPdfModels: any[] = [];
  businessGroups: any[] = [];
  pdfSrc: string | null = null;
  instance = '';

  // Store reference to the listener for cleanup
  private messageListener: ((event: MessageEvent) => void) | null = null;

  ngOnInit(): void {
    // 1. Check if the backend injected initial data (POST workflow)
    const injectedData = (window as any).initialData;
    if (injectedData) {
      console.log('App launched via POST workflow, using injected data');
      // Small timeout to ensure Angular is fully bootstrapped and Router is ready
      setTimeout(() => {
        this.ngZone.run(() => {
          this.handleAuthLogic(injectedData);
        });
      }, 100);
    } else {
      const launchId = this.getLaunchIdFromPath();
      if (launchId) {
        this.loadLaunchSession(launchId);
      } else {
        // 2. Standard iframe/message workflow
        this.setupMessageListener();
        this.sendReadySignal();
      }
    }
  }

  ngOnDestroy(): void {
    // CLEANUP: Remove the global listener when the component is destroyed
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }
  }

  private setupMessageListener(): void {
    this.messageListener = (event: MessageEvent) => {
      const data = event.data ?? {};

      const isAuthPayload =
        data.hasOwnProperty('username') && data.hasOwnProperty('instance');

      if (!isAuthPayload) {
        // This is just noise (webpack, extensions, etc.). Ignore it silently.
        // Do NOT show an error toast here.
        return;
      }

      console.log('Received message event:', data);
      if (!data.username || !data.instance) {
        this.ngZone.run(() => {
          // Show error message via MessageService
          this.messageService.add({
            severity: 'error',
            summary: 'Errore di comunicazione',
            detail: 'Username o Instanza non forniti nel messaggio ricevuto.',
            life: 5000,
          });
        });
        return;
      }

      // CRITICAL: Re-enter the Angular Zone.
      // window events run outside Angular; without this,
      // router.navigate and variable updates might not reflect in the UI immediately.
      this.ngZone.run(() => {
        this.handleAuthLogic(data);
      });
    };

    window.addEventListener('message', this.messageListener);
  }

  private getLaunchIdFromPath(): string | null {
    const match = window.location.pathname.match(/\/launch\/([^/]+)$/);
    return match?.[1] ?? null;
  }

  private getLaunchApiBaseUrl(): string {
    if (window.location.port === '4200') {
      return `${window.location.protocol}//${window.location.hostname}:3000`;
    }

    return `${window.location.protocol}//${window.location.host}`;
  }

  private async loadLaunchSession(launchId: string): Promise<void> {
    const ctx = new HttpContext().set(SKIP_GLOBAL_LOADER, true);

    try {
      const response = await firstValueFrom(
        this.http.get<{ ok: boolean; payload?: any }>(
          `${this.getLaunchApiBaseUrl()}/api/launch/${launchId}`,
          { context: ctx },
        ),
      );

      if (!response?.ok || !response.payload) {
        throw new Error('Launch session payload missing');
      }

      this.ngZone.run(() => {
        this.handleAuthLogic(response.payload);
      });
    } catch (error) {
      console.error('Failed to load launch session:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore avvio',
        detail: 'Impossibile recuperare i dati di avvio.',
        life: 5000,
      });
    }
  }

  private sendReadySignal(): void {
    // Notify the parent window (iframe host) or opener (if new tab)
    const signal = { type: 'APP_READY' };

    if (window.parent && window.parent !== window) {
      window.parent.postMessage(signal, '*');
    } else if (window.opener) {
      window.opener.postMessage(signal, '*');
    }
  }

  private handleAuthLogic(data: any): void {
    const {
      username,
      password,
      firmaRemotaPassword,
      firmaRemotaUsername,
      areaCode = '',
      structureCode = '',
      providerGrafometrica = 'KEFIRMA',
      providerDigitale = 'ARUBA',
      instance = '',
      templateId = -1,
      metadata = {},
      tipologiaDocumento = '',
      informativaId = '',
      nosologicalCode = '',
      codiceApplicativo: ca,
    } = data;

    const codiceApplicativo = ca || 'WL-CONSENSI';

    this.urls.setUrl(this.config.getUrlFor(instance)!);
    this.urls.setBEUrl(this.config.getBEUrlFor(instance)!);

    console.log('Received auth data:', data);

    this.authApi.login$(username, password).subscribe({
      next: (response) => {
        if (response.token) {
          this.tokens.setCredentials(username, password);
          this.tokens.setToken(response.token);
          this.builder.areaCode.set(areaCode);
          this.builder.structureCode.set(structureCode);
          this.builder.instance.set(instance);
          this.builder.digitalSignUsername.set(firmaRemotaUsername);
          this.builder.digitalSignPassword.set(firmaRemotaPassword);
          if (firmaRemotaPassword && firmaRemotaPassword.trim() !== '') {
            this.builder.isPasswordPassedAtStart.set(true);
          }
          this.builder.digitalSignProvider.set(providerDigitale);
          this.builder.graphometricSignProvider.set(providerGrafometrica);
          this.builder.documentType.set(tipologiaDocumento);
          this.builder.parentId.set(informativaId);
          this.builder.nosologicalCode.set(nosologicalCode);
          this.builder.appCode.set(codiceApplicativo);

          // Clean metadata properties
          for (const key in metadata) {
            if (Object.prototype.hasOwnProperty.call(metadata, key)) {
              if (metadata[key] === '') {
                delete metadata[key];
              }
            }
          }

          this.builder.templateId.set(templateId);
          this.builder.initialMetadata.set(metadata);
          this.builder.buId.set(this.config.getBuIdFor(instance)!);

          if (templateId < 0) {
            this.router.navigate(['/templates']);
          } else {
            this.builder.templateId.set(templateId);
            this.router.navigate(['/form']);
          }
        }
      },
      error: (error) => {
        console.error('Login failed:', error);
      },
    });
  }
}
